<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { ListingTypeEnum } from '@/enums/ListingType';

// Interface pour les options de date
interface DateOption {
  value: string;
  label: string;
}

interface DateSearchFieldProps {
  modelValue?: string[] | null
  label?: string
  placeholder?: string
  listingType?: ListingTypeEnum
}

const props = withDefaults(defineProps<DateSearchFieldProps>(), {
  modelValue: null,
  label: '',
  placeholder: '',
  listingType: ListingTypeEnum.Rental
});

const emit = defineEmits<{
  'update:modelValue': [value: string[] | null]
}>();

const { t, locale } = useI18n();

// Dates de disponibilité (même logique que RentalFiltersBar)
const availabilityDates = ref<DateOption[]>([]);

// Générer les dates de disponibilité au montage
const generateAvailabilityDates = () => {
  const dates = [];
  
  // Ajouter l'option par défaut en première position
  dates.push({
    value: '',
    label: t('forms.rentalFilters.selectDate')
  });
  
  const currentDate = new Date();
  
  // Déterminer le mois de départ : si on est après le 1er du mois courant, commencer au mois suivant
  const startOffset = currentDate.getDate() > 1 ? 1 : 0;
  
  for (let i = startOffset; i < 12 + startOffset; i++) {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + i, 1);
    
    // Formater directement avec mois + année
    const formattedDate = date.toLocaleDateString(locale.value, {
      month: 'long',
      year: 'numeric'
    });
    
    dates.push({
      value: date.toISOString().split('T')[0],
      label: formattedDate
    });
  }
  
  availabilityDates.value = dates;
};

const isOpen = ref(false);
const searchTerm = ref('');
const inputRef = ref<HTMLInputElement>();
const containerRef = ref<HTMLInputElement>();

// Dates sélectionnées calculées
const selectedDates = computed(() => {
  if (!props.modelValue || props.modelValue.length === 0) {
    return [];
  }
  return availabilityDates.value.filter(date => props.modelValue!.includes(date.value));
});

// Vérifier si une option est sélectionnée
const isSelected = (dateValue: string) => {
  // Si c'est l'option par défaut (valeur vide), elle est cochée quand aucune date n'est sélectionnée
  if (dateValue === '') {
    return !props.modelValue || props.modelValue.length === 0;
  }
  return props.modelValue?.includes(dateValue) || false;
};

// Affichage de la valeur dans l'input
const displayValue = computed(() => {
  if (selectedDates.value.length === 0) {
    return searchTerm.value || t('forms.rentalFilters.selectDate');
  }
  if (selectedDates.value.length === 1) {
    return selectedDates.value[0].label;
  }
  return t('forms.rentalFilters.selectionCount', selectedDates.value.length);
});

// Filtrage des dates basé sur la recherche
const filteredDates = computed(() => {
  if (!searchTerm.value) {
    return availabilityDates.value;
  }
  return availabilityDates.value.filter(date => {
    return date.label.toLowerCase().includes(searchTerm.value.toLowerCase());
  });
});

const fieldLabel = props.listingType === ListingTypeEnum.Sale ? t('forms.purchaseFilters.deliveryDate') : t('forms.rentalFilters.moveInDate');

const toggleDropdown = () => {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    searchTerm.value = '';
    setTimeout(() => {
      inputRef.value?.focus();
    }, 0);
  }
};

const toggleDate = (date: DateOption) => {
  // Si c'est l'option par défaut (valeur vide), effacer la sélection
  if (date.value === '') {
    emit('update:modelValue', null);
    closeDropdown();
    return;
  }
  
  const currentSelection = props.modelValue || [];
  let newSelection: string[];
  
  if (currentSelection.includes(date.value)) {
    // Désélectionner
    newSelection = currentSelection.filter(value => value !== date.value);
  } else {
    // Sélectionner
    newSelection = [...currentSelection, date.value];
  }
  
  emit('update:modelValue', newSelection.length > 0 ? newSelection : null);
};

const clearSelection = () => {
  emit('update:modelValue', null);
  searchTerm.value = '';
};

const handleInputChange = (event: Event) => {
  const target = event.target as HTMLInputElement;
  searchTerm.value = target.value;
  if (!isOpen.value) {
    isOpen.value = true;
  }
};

const closeDropdown = () => {
  isOpen.value = false;
  searchTerm.value = '';
};

// Fermer le dropdown quand on clique à l'extérieur
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as Element;
  // Éviter de fermer si on clique sur un élément du dropdown
  if (containerRef.value && !containerRef.value.contains(target)) {
    closeDropdown();
  }
};

onMounted(() => {
  // Générer les dates de disponibilité
  generateAvailabilityDates();
  
  // Écouter les clics à l'extérieur avec un délai pour éviter les conflits
  setTimeout(() => {
    document.addEventListener('click', handleClickOutside);
  }, 100);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
});
</script>

<template>
  <div ref="containerRef" class="search-field">
    <label class="field-label">
      {{ props.label || fieldLabel }}
    </label>
    <div class="input-wrapper">
      <input
        ref="inputRef"
        type="text"
        class="search-input"
        :value="displayValue"
        :placeholder="props.placeholder || t('forms.rentalFilters.selectDate')"
        @click="toggleDropdown"
        @input="handleInputChange" />
      <button
        type="button"
        class="dropdown-toggle"
        @click="toggleDropdown">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 448 512"
          :class="{ 'rotated': isOpen }">
          <path d="M201.4 374.6c12.5 12.5 32.8 12.5 45.3 0l160-160c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L224 306.7 86.6 169.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3l160 160z" />
        </svg>
      </button>
      <button
        v-if="selectedDates.length > 0"
        type="button"
        class="clear-button"
        @click="clearSelection">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 384 512">
          <path d="M342.6 150.6c12.5-12.5 12.5-32.8 0-45.3s-32.8-12.5-45.3 0L192 210.7 86.6 105.4c-12.5-12.5-32.8-12.5-45.3 0s-12.5 32.8 0 45.3L146.7 256 41.4 361.4c-12.5 12.5-12.5 32.8 0 45.3s32.8 12.5 45.3 0L192 301.3l105.4 105.4c12.5 12.5 32.8 12.5 45.3 0s12.5-32.8 0-45.3L237.3 256l105.3-105.4z" />
        </svg>
      </button>
    </div>

    <!-- Dropdown des options -->
    <div
      v-if="isOpen"
      class="dropdown-menu">
      <div
        v-if="filteredDates.length === 0"
        class="no-results">
        {{ t('forms.rentalFilters.noDatesFound') }}
      </div>
      <label
        v-for="date in filteredDates"
        :key="date.value"
        class="dropdown-item checkbox-item">
        <input
          type="checkbox"
          :checked="isSelected(date.value)"
          @change="toggleDate(date)"
          class="checkbox" />
        <span class="checkmark"></span>
        <span class="label-text">{{ date.label }}</span>
      </label>
    </div>
  </div>
</template>

<style scoped lang="scss">
@import './search-field-styles.scss';
</style>